import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  capitalize,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { BaseButton } from 'components/BaseButton';
import DatePicker from 'components/date-picker/date-picker';
import NumberField from 'components/number-field';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import React, { useState } from 'react';
import { Control, Controller, UseFormReturn } from 'react-hook-form';
import { TFunction } from 'i18next';
import { DisabledFields } from 'types/disabledFields';
import { listingItemtype } from 'types/ledger';
import { AccountingType } from 'v2-types/order';

import { calculateAdditionalInterest } from '../../services';
import ModalActionBar from 'components/modal-action-bar';
import AdditionalInterest from './components/calculation-helper';
import { EntryLedgerType } from './services/types';

const useStyles = makeStyles((theme: any) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
  },
  title: {
    padding: theme.spacing(3, 3, 0),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2, 2, 0) },
  },
  content: {
    padding: theme.spacing(0, 3),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(0, 2) },
  },
  fields: {
    '& > * ': { marginTop: theme.spacing(2) },
    margin: 0,
  },
  errorIconContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  amountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  amountField: {
    flex: 1,
    minWidth: 0,
  },
  calculatorButton: {
    padding: theme.spacing(1),
    '@media (hover: none) and (pointer: coarse)': {
      width: theme.spacing(5.5),
      height: theme.spacing(5.5),
    },
  },
}));

type AddReceiptProps = {
  open: boolean,
  onClose: () => void,
  handleSubmit: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>,
  control: Control<EntryLedgerType, object>,
  allowedCodes: listingItemtype[],
  allowedKinds: listingItemtype[],
  errors: string[],
  prevData: AccountingType | null,
  t: TFunction;
  loadingEditEntry: boolean,
  loadingAddNewEntry: boolean,
  handleResetValue: () => void,
  methods: UseFormReturn<EntryLedgerType, object>
  additionalamount?: number,
  hideFields?: DisabledFields<EntryLedgerType>
}

const AddEntryScene = ({
  open,
  onClose,
  handleSubmit,
  control,
  allowedCodes,
  allowedKinds,
  errors,
  prevData,
  t,
  loadingEditEntry,
  loadingAddNewEntry,
  handleResetValue,
  methods,
  additionalamount,
  hideFields
}: AddReceiptProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSubmitting = loadingAddNewEntry || loadingEditEntry;
  const prevDataisManual = prevData?.kind?.includes('disbursement') || prevData?.kind?.includes('manual') || prevData?.kind?.includes('receipt');
  const [showCalculator, setshowCalculator] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && !isSubmitting) {
          onClose();
        }
      }}
      disableEscapeKeyDown={isSubmitting}
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
    >
      <form onSubmit={handleSubmit} className={classes.form}>
        <DialogTitle className={classes.title}>
          <Typography variant="h6">
            {`${prevData ? t('accounting:edit-ledger-entry') : t('accounting:add-new-ledger-entry')}`}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <Grid
            container
            className={classes.fields}
            direction="column"
          >
            {!!errors.length && (
              <Grid item xs={12}>
                <Alert
                  severity="error"
                  classes={{ icon: classes.errorIconContainer }}
                >
                  {errors.map((error) => (
                    <Typography key={error} variant="subtitle1">{error}</Typography>
                  ))}
                </Alert>
              </Grid>
            )}
            {prevData
              ? (
                <>
                  <Box className={classes.amountRow}>
                    <Box className={classes.amountField}>
                      <Controller
                        name="amount_override"
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                          <NumberField
                            thousandSeparator
                            prefix="$"
                            customRef={ref}
                            label={t('accounting:amount-override')}
                            disabled={showCalculator || isSubmitting}
                            value={field.value}
                            onValueChange={({ floatValue }) => field.onChange(floatValue)}
                          />
                        )}
                      />
                    </Box>
                    {prevData?.code.includes('additional') && (
                      <IconButton
                        className={classes.calculatorButton}
                        aria-label={t('payoffs:additional-interest-title')}
                        disabled={isSubmitting}
                        onClick={() => { setshowCalculator(true); }}
                        size="large"
                      >
                        <FontAwesomeIcon icon="calculator" size="sm" />
                      </IconButton>
                    )}
                  </Box>
                  {showCalculator && (
                    <AdditionalInterest
                      additionalAmount={additionalamount || 0}
                      handleCancel={() => {
                        setshowCalculator(false);
                      }}
                      handleSave={(data) => {
                        const {
                          interest_rate,
                          days_per_year,
                          from_date, to_date,
                          per_diem
                        } = data;
                        const { additionalInterest } = calculateAdditionalInterest(additionalamount || 0, interest_rate, days_per_year);
                        const additional_interes = additionalInterest(from_date as any as Date, to_date as any as Date, interest_rate === 0 ? per_diem : undefined);
                        methods.setValue('amount_override', additional_interes);
                        setshowCalculator(false);
                      }}
                    />
                  )}
                </>
              )

              : (
                <Controller
                  name="amount"
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      label={t('accounting:amount')}
                      disabled={isSubmitting}
                    />
                  )}
                />
              )}
            {prevData
              ? (
                <Controller
                  name="description"
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      label={t('accounting:description')}
                      disabled={!!prevData || isSubmitting}
                      error={!!methods.formState.errors.description}
                      value={prevData?.code.includes('mortgage') ? capitalize(prevData.code.split('_')[1] || '') : field.value}
                    />
                  )}
                />
              )
              : (
                <Controller
                  name="description"
                  control={control}
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      label={t('accounting:description')}
                      disabled={!!prevData || isSubmitting}
                      error={!!methods.formState.errors.description}
                    />
                  )}
                />
              )}

            {!prevData
              && (
                <>
                  {!hideFields?.code && (
                    <Controller
                      name="code"
                      control={control}
                      render={({ field: { ref, onChange, ...field } }) => (
                        <SelectField
                          {...field}
                          inputRef={ref}
                          value={allowedCodes.length > 0 ? field.value : ''}
                          data={allowedCodes}
                          dataKey="code"
                          dataValue="code"
                          dataText="description"
                          label={t('accounting:code')}
                          handleChange={onChange}
                          disabled={isSubmitting}
                          error={!!methods.formState.errors.description}
                        />
                      )}
                    />
                  )}
                  {!hideFields?.kind && (
                    <Controller
                      name="kind"
                      control={control}
                      render={({ field: { ref, onChange, ...field } }) => (
                        <SelectField
                          {...field}
                          inputRef={ref}
                          value={allowedKinds.length > 0 ? field.value : ''}
                          data={allowedKinds}
                          dataKey="code"
                          dataValue="code"
                          dataText="description"
                          label={t('accounting:kind')}
                          handleChange={onChange}
                          disabled={isSubmitting}
                          error={!!methods.formState.errors.description}
                        />
                      )}
                    />
                  )}
                </>
              )}
            <Controller
              name="entryDate"
              control={control}
              render={({ field: { ref, value, ...field } }) => (
                <DatePicker
                  {...field}
                  inputRef={ref}
                  disableToolbar={false}
                  label={t('accounting:entry-date')}
                  disabled={isSubmitting}
                  value={value as string | Date}
                />
              )}
            />
          </Grid>
        </DialogContent>
        <ModalActionBar
          onCancel={onClose}
          submitText={`${prevData ? t('accounting:edit-entry') : t('accounting:add-entry')}`}
          isSubmitting={isSubmitting}
          submitDisabled={!methods.formState.isValid}
          leftAction={prevData && !prevDataisManual ? (
            <BaseButton
              buttonVariant="outlined"
              buttonColor="inherit"
              icon="undo"
              text={t('accounting:reset-value')}
              disabled={isSubmitting}
              onClick={handleResetValue}
            />
          ) : undefined}
        />
      </form>
    </Dialog>
  );
};

export default AddEntryScene;
